import React from 'react';
import ReactDOM from 'react-dom';
import { ReactEditor, useReadOnly } from 'slate-react';
import {
  type EditorPlugin,
  type EventHandler,
  type LayoutComponent,
} from '../../types/aha-editor';
import { ApplicationView, observable, observe } from '@aha-app/mvc';
import {
  createPopper,
  type Instance,
  type VirtualElement,
} from '@popperjs/core';
import type AhaEditorV2Controller from '../../controllers/AhaEditorV2Controller';
import {
  Editor,
  type Location,
  Node,
  Range,
  type RangeRef,
  Transforms,
} from 'slate';
import { cancelEventHandlersOutsideFocusableElements } from '../../util/mouseHandlers';
import runAfterSlate from '../../util/runAfterSlate';
import VisibleMenuWrapper from './VisibleMenuWrapper';
import { type MenuPluginOptions, type MenuState } from './types';

// The MenuPluginFactory is a factory function that creates a plugin that renders
// a menu appearing when a specific trigger character is typed. The menu is rendered using a popper
// in a portal.
// The menu can be used to search for and select items. It is used by several plugins, including:
// - Aha! Record Reference Plugin
// - Aha! User Reference Plugin
// - Editor command menu
// - Emoji menu
// - Aha! Discovery email template variables

const OPEN_MENUS = new WeakMap<Editor, string>();

const MenuPluginFactory =
  <MenuItemObject, SearchResult = MenuItemObject>({
    name,
    triggerCharacter,
    onSearch,
    onSelect,
    MenuItemComponent,
    dependencies,
    menuItemUniqueKey,
    minCharacters = 0,
    classNames,
    searchingPlaceholder = 'Searching...',
  }: MenuPluginOptions<MenuItemObject, SearchResult>) =>
  (): EditorPlugin => {
    // The local plugin state for this menu
    const menuState = observable<MenuState>({
      showMenu: false,
      text: null,
    });

    // Cache search results and last menu items for this plugin instance
    const searchResults = new Map<string, any>();
    const lastMenuItems = new Map<string, any>();

    let editor: Editor;
    let controller: AhaEditorV2Controller;
    let instance: Instance | undefined;
    let keydownHandler: ((event: KeyboardEvent) => boolean | undefined) | null =
      null;
    let rangeRef: RangeRef | null = null;
    // Container for the popper content
    const popper = document.createElement('div');
    popper.setAttribute('popover', 'manual');
    const virtualElement: VirtualElement = {
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        x: 0,
        y: 0,
        toJSON: () => '',
      }),
      contextElement: document.body,
    };

    const setKeydownHandler = (
      handler: ((event: KeyboardEvent) => boolean | undefined) | null
    ) => {
      keydownHandler = handler;
    };

    const deleteSearchText = (editor: Editor) => {
      if (rangeRef?.current) {
        Transforms.delete(editor, {
          at: rangeRef.current,
        });
      }
    };

    const currentText = (editor: Editor) => {
      const range = rangeRef?.current;
      if (!range) return '';

      return Editor.string(editor, range);
    };

    const currentTextNode = (editor: Editor, at: Location) => {
      return Editor.node(editor, at);
    };

    const getSearchText = (editor: Editor) => {
      if (!editor.selection || Range.isExpanded(editor.selection)) return null;

      const text = currentText(editor);

      return text.slice(triggerCharacter.length);
    };

    const MenuPluginWrapper: LayoutComponent = () => {
      const { showMenu, text } = menuState;
      const readOnly = useReadOnly();

      let content: React.ReactNode = null;

      if (
        showMenu &&
        text.length - triggerCharacter.length >= minCharacters &&
        !readOnly
      ) {
        content = (
          <div
            // Interactions within the editor should never close the drawer.
            data-skip-drawer-close
            {...cancelEventHandlersOutsideFocusableElements}
          >
            <VisibleMenuWrapper
              editor={editor}
              controller={controller}
              hideMenu={hideMenu}
              searchText={getSearchText(editor)}
              setKeydownHandler={setKeydownHandler}
              name={name}
              onSearch={onSearch}
              onSelect={({ item, event }) => {
                deleteSearchText(editor);
                hideMenu();
                onSelect({
                  item,
                  event,
                  editor,
                  controller,
                });
              }}
              MenuItemComponent={MenuItemComponent}
              menuItemUniqueKey={menuItemUniqueKey}
              classNames={classNames}
              searchingPlaceholder={searchingPlaceholder}
              searchResults={searchResults}
              lastMenuItems={lastMenuItems}
            />
          </div>
        );
      }

      return <>{popper && ReactDOM.createPortal(content, popper)}</>;
    };

    const onKeyDown: EventHandler<KeyboardEvent> = (event, ahaEditor) => {
      const { editor, controller } = ahaEditor;
      if (!editor.selection) return;

      switch (event.key) {
        case triggerCharacter: {
          // If another menu plugin is already open for this editor instance, do not open this one.
          // For example, if the emoji menu is open and the user types / we should not open the command menu.
          const openMenu = OPEN_MENUS.get(editor);
          if (openMenu && openMenu !== name) return;

          const textNodeEntry = currentTextNode(editor, editor.selection.focus);
          const text = Node.string(textNodeEntry[0]).slice(
            0,
            editor.selection.focus.offset
          );
          if (text === '' || text[text.length - 1] === ' ') {
            showMenu(editor, controller);
            OPEN_MENUS.set(editor, name);
          }
          break;
        }
        case 'Escape':
          if (menuState.showMenu) {
            hideMenu();
          }
          break;
        case 'Backspace':
          if (menuState.showMenu && getSearchText(editor) === '') {
            hideMenu();
          }
          break;
        default: {
          if (keydownHandler) {
            return keydownHandler(event);
          }
        }
      }

      return false;
    };

    function hideMenu() {
      if (!menuState.showMenu) return;

      Object.assign(menuState, {
        showMenu: false,
        text: null,
      });
      OPEN_MENUS.delete(editor);

      instance?.update();

      popper?.hidePopover();
    }

    function showMenu(editor: Editor, controller: AhaEditorV2Controller) {
      if (!editor.selection) return;

      if (menuState.showMenu) {
        // If we're already showing the menu, bail out
        runAfterSlate(() => {
          instance?.update();
        });
        return;
      }

      if (!controller.state.focused || controller.state.readOnly) {
        hideMenu();
        return;
      }

      if (rangeRef) {
        // Always call .unref() when done with a PathRef
        rangeRef.unref();
      }
      rangeRef = Editor.rangeRef(editor, editor.selection, {
        affinity: 'outward',
      });

      const text = currentText(editor);

      Object.assign(menuState, {
        showMenu: true,
        text,
      });

      updateMenuState();

      window.requestAnimationFrame(() => {
        instance?.update();
        popper.showPopover();
      });
    }

    // Used to set up the popper. We run this whenever the virtual element changes.
    function updatePopperElement(virtualElement?: VirtualElement) {
      if (
        virtualElement &&
        virtualElement !== instance?.state.elements.reference
      ) {
        if (instance) instance.destroy();

        instance = createPopper(virtualElement, popper, {
          modifiers: [
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top-start'],
              },
            },
          ],
          strategy: 'fixed',
          placement: 'bottom-start',
        });
      }
    }

    function updateMenuState(retry = true) {
      if (!menuState.showMenu) return;

      const { selection } = editor;
      const range = rangeRef?.current;
      const rangeIncludesSelection =
        selection && range && Range.includes(range, selection?.focus);

      // If the range no longer includes the selection, hide the menu.
      // This will happen if the user moves the cursor or deletes the text.
      if (!rangeIncludesSelection) {
        hideMenu();
        return;
      }

      menuState.text = currentText(editor);

      try {
        const domRange = ReactEditor.toDOMRange(editor, range);
        const domRangeRect = domRange.getBoundingClientRect();

        virtualElement.getBoundingClientRect = () => ({
          width: domRangeRect.width,
          height: domRangeRect.height,
          top: domRangeRect.top,
          bottom: domRangeRect.bottom,
          left: domRangeRect.left,
          right: domRangeRect.right,
          x: domRangeRect.left,
          y: domRangeRect.top,
          toJSON: () => '',
        });
      } catch (ex) {
        // Error finding the DOM node, it might be because the node was just added.
        // Retry just once, but after a delay.
        if (retry) {
          runAfterSlate(() => updateMenuState(false));
        }

        return;
      }

      updatePopperElement(virtualElement);
    }

    return {
      name: `Menu plugin component - ${name}`,
      dependencies,
      layoutComponent: {
        Component: ApplicationView(MenuPluginWrapper),
        location: 'below',
      },
      handlers: {
        onKeyDown,
        onInit(_ahaEditor) {
          editor = _ahaEditor.editor;
          controller = _ahaEditor.controller;
        },

        onMount() {
          controller.editorContainer.appendChild(popper);

          const observer = observe(() => {
            // Hide menu if the editor becomes blurred or read-only
            if (controller.state.focused && !controller.state.readOnly) {
              updateMenuState();
            } else {
              hideMenu();
            }
          });

          return () => {
            observer();
          };
        },

        onChange() {
          updateMenuState();
        },
      },
    };
  };

export default MenuPluginFactory;
